import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  LinearProgress,
  CircularProgress,
  Alert
} from '@mui/material';
import { BarChart, ChevronRight } from '@mui/icons-material';

const FunctionalUnitDistribution = ({ 
  functionalUnits, 
  totalAccounts, 
  loading, 
  error,
  onUnitClick 
}) => {
  // Color palette for different functional units
  const colors = [
    '#2196F3', // Blue
    '#4CAF50', // Green
    '#FF9800', // Orange
    '#9C27B0', // Purple
    '#009688', // Teal
    '#E91E63', // Pink
    '#FFC107', // Amber
    '#00BCD4', // Cyan
    '#8BC34A', // Light Green
    '#FF5722', // Deep Orange
    '#757575'  // Grey (for others)
  ];
  
  const getColor = (index) => {
    return colors[index % colors.length];
  };
  
  const handleUnitClick = (unitName) => {
    if (onUnitClick) {
      onUnitClick(unitName);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading distribution...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
          Failed to load distribution
        </Alert>
      </Box>
    );
  }
  
  if (functionalUnits.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
          No data available
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 1.5,
        pb: 1,
        borderBottom: '2px solid',
        borderColor: 'divider'
      }}>
        <BarChart color="primary" />
        <Typography variant="subtitle2" fontWeight={600}>
          By Functional Unit
        </Typography>
      </Box>
      
      <List dense disablePadding>
        {functionalUnits.map((unit, index) => (
          <ListItem 
            key={unit.name} 
            disablePadding
            sx={{ mb: 1.5 }}
          >
            <ListItemButton
              onClick={() => handleUnitClick(unit.name)}
              sx={{
                borderRadius: 1,
                px: 1.5,
                py: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 0.5,
                width: '100%'
              }}>
                <Typography 
                  variant="body2" 
                  fontWeight={500}
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '60%'
                  }}
                >
                  {unit.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {unit.count}
                  </Typography>
                  <ChevronRight fontSize="small" sx={{ color: 'action.active' }} />
                </Box>
              </Box>
              
              <Box sx={{ width: '100%' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={unit.percentage} 
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getColor(index),
                      borderRadius: 1
                    }
                  }}
                />
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ mt: 0.25, display: 'block' }}
                >
                  {unit.percentage}%
                </Typography>
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default FunctionalUnitDistribution;
